import React, { useState } from "react";
import { Modal, Form, Input, Button, Alert, Typography } from "antd";
import { LockOutlined, WarningOutlined } from "@ant-design/icons";
import axios from "axios";

const { Text, Title } = Typography;

const PasswordChangeModal = ({ visible, userId, onPasswordChanged }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const token = localStorage.getItem("token");

  const handleSubmit = async (values) => {
    setLoading(true);
    setError("");

    try {
      // Update password
      await axios.patch(
        `https://zedlenders.pickmesms.com/users/${userId}`,
        {
          password: values.newPassword,
          needs_password_change: false // Clear the flag
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      form.resetFields();
      onPasswordChanged();
    } catch (err) {
      const errorMessage =
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data?.message ||
        "Failed to change password. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <WarningOutlined style={{ color: "#faad14", fontSize: 20 }} />
          <Title level={4} style={{ margin: 0 }}>
            Password Change Required
          </Title>
        </div>
      }
      visible={visible}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={500}
    >
      <Alert
        message="Default Password Detected"
        description="For security reasons, you must change your password before continuing. Please create a strong password."
        type="warning"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form form={form} layout="vertical" onFinish={handleSubmit} size="large">
        <Form.Item
          label="New Password"
          name="newPassword"
          rules={[
            {
              required: true,
              message: "Please enter your new password",
            },
            {
              min: 8,
              message: "Password must be at least 8 characters",
            },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
              message:
                "Password must contain at least one uppercase letter, one lowercase letter, and one number",
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter new password (min 8 characters)"
            autoComplete="new-password"
          />
        </Form.Item>

        <Form.Item
          label="Confirm New Password"
          name="confirmPassword"
          dependencies={["newPassword"]}
          rules={[
            {
              required: true,
              message: "Please confirm your password",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Passwords do not match"));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Confirm new password"
            autoComplete="new-password"
          />
        </Form.Item>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError("")}
            style={{ marginBottom: 16 }}
          />
        )}

        <div
          style={{
            padding: "12px 16px",
            backgroundColor: "#f0f5ff",
            borderRadius: 6,
            marginBottom: 16,
          }}
        >
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            Password Requirements:
          </Text>
          <ul
            style={{
              margin: 0,
              paddingLeft: 20,
              fontSize: 13,
              color: "#666",
            }}
          >
            <li>At least 8 characters long</li>
            <li>Contains uppercase and lowercase letters</li>
            <li>Contains at least one number</li>
            <li>Avoid using common words or personal information</li>
          </ul>
        </div>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            size="large"
            style={{
              height: 48,
              fontWeight: 600,
            }}
          >
            {loading ? "Changing Password..." : "Change Password & Continue"}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PasswordChangeModal;
